/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useMemo, useState } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Column } from "react-table";
import { useStateMachine } from "little-state-machine";

import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import { getInvoiceProviders, postInvoiceAuditLogMessage } from "api";
import { useGetInvoicelist } from "api/queries/invoice/useGetInvoiceList";
import RefreshCache from "components/molecules/RefreshCache/RefreshCache";
import Filter from "components/organisms/Filter/Filter";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import getLabel from "helpers/getLabel";
import moment from "moment";
import { DateTimeFormat } from "helpers/dateTimeHelper";
import { useFormatPreferredDatetime } from "hooks/useFormatPreferredDatetime";
import Badge from "components/atoms/Badge/Badge";
import ListViewTour from "components/tours/listView/listViewTour";
import { formatNumber } from "helpers/commonHelpers";
import errorToast from "helpers/errorToast";
import { toast } from "react-toastify";
import { ReactComponent as ApproveIcon } from "assets/images/icons/TopNavIcons/SubmitPOD.svg";
import { ReactComponent as DeclineIcon } from "assets/images/icons/CircleCross.svg";
import { InvoiceStatusColors } from "constants/";
import { updateShipmentIdAndTitle } from "store/store";
import useInvoiceFilters from "hooks/filters/useInvoiceFilters";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";

function Invoices() {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const { config: filterConfig } = useInvoiceFilters();
  const { persistedColumnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "invoice" });
  const { formatPreferredDatetime } = useFormatPreferredDatetime(DateTimeFormat.fullDateTimeFormat);
  const status: string = searchParams.get("display_status") || "";
  const canAudit = JSON.parse(permissions || "{}").permissions?.invoicing?.audit_invoices;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [providerFilterOptions, setProviderFilterOptions] = useState<{ value: string; label: string }[]>([]);
  const [refreshToken, setRefreshToken] = useState(false);

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.invoicing.view_invoices) navigate("/access-denied");
    else {
      getFilterProviderOptions();
    }
  }, []);

  const getFilterProviderOptions = () => {
    getInvoiceProviders()
      .then((res) => {
        const providersData: { id: number; company_name: string }[] = res.data;
        setProviderFilterOptions(
          providersData.map((provider) => ({ value: provider.id.toString(), label: provider.company_name })),
        );
      })
      .catch((err) => errorToast(err));
  };

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("invoices_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
  };

  const columns = useMemo<Column[]>(
    () => [
      {
        id: "title",
        Header: intl.formatMessage({
          id: "general.shipment",
          defaultMessage: "Shipment",
        }),
        accessor: "shipment_reference",
        sortable: true,
        Cell: ({ value, row }: any) => {
          if (!value && !row.original?.shipment_title) return <p className="mb-0">-</p>;
          return (
            <>
              <p className="font-b4 mb-1">
                {value || "-"} / {row.original?.invoice_reference}
              </p>
              <span>{row.original?.shipment_title}</span>
            </>
          );
        },
      },
      {
        Header: intl.formatMessage(
          {
            id: "general.paymentLocation",
            defaultMessage: "Payment location",
          },
          { paymentLocation: getLabel("Payment location", "capitalize-first") },
        ),
        accessor: "payment_location",
        sortable: true,
        Cell: ({ value }) => <p className="mb-0">{value}</p>,
      },
      {
        Header: intl.formatMessage({
          id: "invoices.organizationTableHeader",
          defaultMessage: "Organization",
        }),
        accessor: "provider_name",
        sortable: false,
        Cell: ({ value, row }: any) => (
          <>
            <p className="mb-2">{value}</p>
            <span>{row.original?.provider_invoice_number}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({
          id: "invoices.invoicedAmount",
          defaultMessage: "Invoiced amount",
        }),
        accessor: "expected_amount_due",
        sortable: true,
        Cell: ({ row }: any) => {
          const expectedAmount = row.original?.expected_amount_due;
          const quotedAmount = row.original?.total_quoted_amount;
          const changeInValue = expectedAmount - quotedAmount;
          const color = changeInValue > 0 ? "#EA7939" : "#9EC728";
          const prefix = changeInValue > 0 ? "+" : "";

          let percentageDiff = null;
          if (changeInValue !== 0 && expectedAmount !== 0 && quotedAmount !== 0) {
            percentageDiff = (
              <span className="font-weight-normal ml-2 font-b3" style={{ color }}>
                ({prefix}
                {((changeInValue / quotedAmount) * 100).toFixed(1)}%)
              </span>
            );
          }
          return (
            <p>
              {formatNumber(expectedAmount, 2)} {row.original?.expected_amount_due_currency}
              {percentageDiff}
            </p>
          );
        },
      },
      {
        Header: intl.formatMessage({
          id: "tableHeaders.submittedDate",
          defaultMessage: "Submitted date",
        }),
        accessor: "submit_date",
        sortable: true,
        Cell: ({ value }: any) => {
          const submitDate = moment(value).format("DD MMM YYYY HH:mm");
          return <p className="mb-2">{submitDate}</p>;
        },
      },
      {
        Header: intl.formatMessage({
          id: "tableHeaders.lastModifiedDate",
          defaultMessage: "Last modified date",
        }),
        accessor: "modified",
        sortable: true,
        Cell: ({ value }: any) => <p className="mb-0">{formatPreferredDatetime(value)}</p>,
      },
      {
        Header: intl.formatMessage({
          id: "tableHeaders.createdDate",
          defaultMessage: "Created date",
        }),
        accessor: "created",
        sortable: true,
        Cell: ({ value }: any) => <p className="mb-0">{formatPreferredDatetime(value)}</p>,
      },
      ...(status === "approved"
        ? [
            {
              Header: intl.formatMessage({
                id: "report.ApprovedDate",
                defaultMessage: "Approved date",
              }),
              accessor: "approved_date",
              sortable: true,
              Cell: ({ value }: any) => <p className="mb-0">{formatPreferredDatetime(value)}</p>,
            },
          ]
        : []),
      {
        id: "status",
        Header: intl.formatMessage({
          id: "general.status",
          defaultMessage: "Status",
        }),
        accessor: "display_status",
        sortable: true,
        Cell: ({ value: cellValue }) =>
          cellValue ? (
            <div className="d-flex flex-column align-items-start">
              <Badge type={InvoiceStatusColors[cellValue.value] || "secondary"} label={cellValue.label} />
            </div>
          ) : (
            "-"
          ),
      },
      ...(canAudit
        ? [
            {
              Header: "",
              actions: true,
              accessor: "shipment_reference",
              Cell: ({ row }: any) => {
                const { invoice_status: invoiceStatus, invoice_reference: invoiceReference } = row.original;
                if (invoiceStatus !== "Awaiting Audit") return;

                const [isActionInProgress, setIsActionInProgress] = useState(false);

                const approveInvoice = (isApproved: boolean) => {
                  setIsActionInProgress(true);
                  const payload = {
                    comment: "",
                    action: isApproved ? "approved" : "declined",
                  };
                  postInvoiceAuditLogMessage(invoiceReference, payload)
                    .then(() => {
                      refetch();
                      toast(isApproved ? "Invoice has been approved" : "Invoice has been declined", { type: "success" });
                    })
                    .catch((err) => errorToast(err))
                    .finally(() => setIsActionInProgress(false));
                };

                if (isActionInProgress) return <div className="spinner-border spinner-border-sm mr-2" role="status" />;
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 0 }}
                      overlay={
                        <Tooltip id="button-tooltip">
                          <FormattedMessage id="general.approve" defaultMessage="Approve" />
                        </Tooltip>
                      }
                    >
                      <ApproveIcon width="18" height="18" onClick={() => approveInvoice(true)} />
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 0 }}
                      overlay={
                        <Tooltip id="button-tooltip">
                          <FormattedMessage id="general.decline" defaultMessage="Decline" />
                        </Tooltip>
                      }
                    >
                      <DeclineIcon
                        className="ml-2"
                        style={{ stroke: "black" }}
                        width="18"
                        height="18"
                        onClick={() => approveInvoice(false)}
                      />
                    </OverlayTrigger>
                  </div>
                );
              },
            } as Column,
          ]
        : []),
    ],
    [status, intl, canAudit, formatPreferredDatetime],
  );

  const { instance, fetching, totalData, refetch, dataUpdatedAt, refetchEndpoint } = useTableQueryRequest(
    useGetInvoicelist,
    columns,
    {
      hiddenColumns: getHiddenColumns(),
      columnOrder: persistedColumnOrder,
      params: {
        status,
        ...(refreshToken && { refresh_token: true }),
      },
      filters: (globalFilter) => ({
        ...(globalFilter.query?.length && { query: globalFilter.query }),
        ...(globalFilter.display_status?.length && { status: globalFilter.display_status.join(",") }),
        ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location.join(",") }),
        ...(globalFilter.provider?.length && { provider: globalFilter.provider.join(",") }),
      }),
    },
  );

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("display_status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  useEffect(() => {});

  const handleRowClick = async (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.shipment_reference;
    const title = row.original?.shipment_title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    if (e.ctrlKey || e.metaKey) window.open(`/shipment/${reference}/billing`, "_blank");
    else navigate(`/shipment/${reference}/billing`, { state: { data: row.original?.created_by } });
  };

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("display_status");
    } else {
      searchParams.set("display_status", eventKey || "");
    }
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  const handleRefresh = () => {
    if (!refreshToken) setRefreshToken(true);
    else {
      refetchEndpoint();
    }
  };

  return (
    <>
      <ListViewTour />
      <Card body className="shadow-none quote-container">
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-wrap align-items-center mb-4">
            <h3 className="mr-3">
              <FormattedMessage id="general.invoices" defaultMessage="Invoices" />
            </h3>
            <GlobalTableSearch
              currentFilters={instance.state.globalFilter}
              setFilters={instance.setGlobalFilter}
              filterKey="query"
              eventContext="Invoice List"
            />
          </div>
          <RefreshCache dataUpdatedAt={dataUpdatedAt} onRefresh={handleRefresh} isLoading={fetching} />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="quote-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                { eventKey: "", title: intl.formatMessage({ id: "tabs.all", defaultMessage: "All" }) },
                {
                  eventKey: "awaiting_audit",
                  title: intl.formatMessage({ id: "tabs.awaitingAudit", defaultMessage: "Awaiting Audit" }),
                },
                { eventKey: "approved", title: intl.formatMessage({ id: "tabs.approved", defaultMessage: "Approved" }) },
                { eventKey: "declined", title: intl.formatMessage({ id: "tabs.declined", defaultMessage: "Declined" }) },
                {
                  eventKey: "removed,auto_removed",
                  title: intl.formatMessage({ id: "tabs.removed", defaultMessage: "Removed" }),
                },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                eventContext="Invoice List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter columns={instance.allColumns} eventContext="Invoice List" listViewName="invoices" />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default Invoices;
